import { Suspense, lazy, useEffect } from 'react';
import toast from 'react-hot-toast';
import i18n from '@/lib/i18n';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { useAuthStore } from '@/stores/auth.store';
import { safeReturnPath } from '@/lib/return-path';
import { useI18nStore, applyDocumentLanguage } from '@/stores/i18n.store';
import { usePageLogger } from '@/hooks/usePageLogger';
import { useOfflineSyncRunner } from '@/hooks/useOfflineSync';
import { useSocket } from '@/hooks/useSocket';
import { isPhonePath } from '@/lib/phone-path';
import { OfflineBanner } from '@/components/ui/OfflineBanner';
import { OfflineSyncPanel } from '@/components/ui/OfflineSyncPanel';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { WorkspaceLayout, WorkspaceIndexRedirect } from '@/components/layout/WorkspaceLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { POSPage } from '@/pages/pos/POSPage';
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { TaxGuidePage } from '@/pages/settings/TaxGuidePage';
import { PlatformPage } from '@/pages/platform/PlatformPage';
import { KitchenDisplayPage } from '@/pages/restaurant/KitchenDisplayPage';
import { CustomerFacingDisplayPage } from '@/pages/pos/CustomerFacingDisplayPage';
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage';
import { PortalDashboardPage } from '@/pages/portal/PortalDashboardPage';
import { KioskPage } from '@/pages/kiosk/KioskPage';
const MobileApp = lazy(() => import('@/pages/mobile/MobileApp'));
import { MenuPage } from '@/pages/menu/MenuPage';
import { OnlineOrderPage } from '@/pages/order/OnlineOrderPage';
import { WORKSPACES, LEGACY_REDIRECTS, LEGACY_DETAIL_REDIRECTS } from '@/config/navigation';

/** Exact-path legacy hop — keep query, hash, and router state (e.g. PO prefill). */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  return (
    <Navigate
      to={`${to}${location.search}${location.hash}`}
      replace
      state={location.state}
    />
  );
}

/** Param-bearing legacy hop — `/recipes/:id` → `/manufacturing/recipes/:id`. */
function LegacyParamRedirect({ toPrefix }: { toPrefix: string }) {
  const { id } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`${toPrefix}/${id}${location.search}${location.hash}`}
      replace
      state={location.state}
    />
  );
}

/**
 * Sends a signed-out visitor to /login and remembers the page they asked for, so signing in returns
 * there (POS-I186). The path travels in router state, not in the URL.
 */
function LoginRedirect({ from }: { from: { pathname: string; search: string } }) {
  return <Navigate to="/login" replace state={{ from: `${from.pathname}${from.search}` }} />;
}

/**
 * POS-I340: a page the role may not open sends the user Home and says why (one toast, fixed id,
 * so repeated redirects do not stack).
 */
function AccessDeniedRedirect() {
  useEffect(() => {
    toast.error(i18n.t('settings:noAccessToPage'), { id: 'access-denied' });
  }, []);
  return <Navigate to="/dashboard" replace />;
}

/** Redirects to /login if not authenticated. */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  if (!isAuthenticated) return <LoginRedirect from={location} />;
  return <>{children}</>;
}

/**
 * Redirects to /dashboard if the user lacks the required permission.
 * This is the real backend-enforcing UI boundary — backend @RequirePermissions
 * already enforces at API level; this provides the UX redirect layer.
 * Supports `|` (OR) and `&&` (AND) permission expressions.
 */
function PermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const location = useLocation();
  if (!isAuthenticated) return <LoginRedirect from={location} />;
  const allowed = permission.includes('&&')
    ? permission.split('&&').every((p) => hasPermission(p.trim()))
    : permission.split('|').some((p) => hasPermission(p.trim()));
  if (!allowed) return <AccessDeniedRedirect />;
  return <>{children}</>;
}

/** Redirects to /dashboard if the user is not a super-admin. */
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!isAuthenticated) return <LoginRedirect from={location} />;
  if (!user?.isSuperAdmin) return <AccessDeniedRedirect />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  if (isAuthenticated) return <Navigate to={safeReturnPath((location.state as { from?: unknown } | null)?.from)} replace />;
  return <>{children}</>;
}

/** Permission refresh hook — runs on mount, tab focus, and every 60 s. */
function usePermissionSync() {
  const refreshPermissions = useAuthStore((s) => s.refreshPermissions);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Immediate refresh on load / re-hydration
    refreshPermissions();

    // Refresh when tab becomes visible again (user switches back from another tab)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshPermissions();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Periodic refresh every 60 s to pick up subscription changes
    const interval = setInterval(refreshPermissions, 60_000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [isAuthenticated, refreshPermissions]);
}

/** Syncs i18n language/direction state to DOM on initial load. */
function useRtlSync() {
  const language = useI18nStore((s) => s.language);
  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);
}

export default function App() {
  usePageLogger();
  usePermissionSync();
  useOfflineSyncRunner();
  useSocket();
  useRtlSync();
  const location = useLocation();

  return (
    <>
      {/* POS-I267: the phone (/m) has its own offline messages; the desktop POS banner never shows there */}
      {!isPhonePath(location.pathname) && <OfflineBanner />}
      {!isPhonePath(location.pathname) && <OfflineSyncPanel />}
      <Routes>
        {/* Guest routes */}
        <Route element={<GuestRoute><ErrorBoundary><AuthLayout /></ErrorBoundary></GuestRoute>}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>
        {/* POS-I185: reachable while signed in too (a shared till, or a link opened in the same browser) */}
        <Route element={<ErrorBoundary><AuthLayout /></ErrorBoundary>}>
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* POS — full screen, no sidebar */}
        <Route path="/pos" element={<PermissionRoute permission="pos:create"><ErrorBoundary><POSPage /></ErrorBoundary></PermissionRoute>} />

        {/* CFD — Customer-Facing Display, full-screen public display (no auth required) */}
        <Route path="/cfd" element={<ErrorBoundary><CustomerFacingDisplayPage /></ErrorBoundary>} />

        {/* Kiosk — Self-service kiosk mode, full screen, no sidebar */}
        <Route path="/kiosk" element={<PermissionRoute permission="pos:create"><ErrorBoundary><KioskPage /></ErrorBoundary></PermissionRoute>} />

        {/* Public Menu — QR self-ordering, no auth required */}
        <Route path="/menu" element={<ErrorBoundary><MenuPage /></ErrorBoundary>} />
        {/* Public Online Ordering — embeddable widget page, no auth required */}
        <Route path="/order/:slug" element={<ErrorBoundary><OnlineOrderPage /></ErrorBoundary>} />

        {/* KDS — full screen kiosk, no sidebar */}
        <Route path="/kitchen" element={<PermissionRoute permission="kitchen:read"><ErrorBoundary><KitchenDisplayPage /></ErrorBoundary></PermissionRoute>} />

        {/* Protected routes with dashboard layout */}
        <Route element={<ProtectedRoute><ErrorBoundary><DashboardLayout /></ErrorBoundary></ProtectedRoute>}>
          {/* Always accessible when logged in */}
          <Route path="/dashboard" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
          <Route path="/settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
          <Route path="/store-guide" element={<ErrorBoundary><TaxGuidePage /></ErrorBoundary>} />
          <Route path="/platform" element={<SuperAdminRoute><ErrorBoundary><PlatformPage /></ErrorBoundary></SuperAdminRoute>} />

          {/* Reports — top-level hub (also reachable contextually from workspaces) */}
          <Route path="/reports" element={<PermissionRoute permission="reports:read"><ErrorBoundary><ReportsPage /></ErrorBoundary></PermissionRoute>} />

          {/* Workspace routes — generated from the navigation single-source-of-truth */}
          {WORKSPACES.map((ws) => (
            <Route key={ws.id} path={ws.basePath} element={<WorkspaceLayout />}>
              <Route index element={<WorkspaceIndexRedirect workspace={ws} />} />
              {ws.tabs.map((tab) => {
                const Component = tab.Component;
                const element = (
                  <ErrorBoundary>
                    <Component />
                  </ErrorBoundary>
                );
                return (
                  <Route
                    key={tab.key}
                    path={tab.segment}
                    element={
                      tab.permission
                        ? <PermissionRoute permission={tab.permission}>{element}</PermissionRoute>
                        : element
                    }
                  />
                );
              })}
            </Route>
          ))}

          {/* Legacy exact-path redirects — keep old deep links working post-migration */}
          {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
          ))}
          {/* Legacy param-bearing detail redirects — preserve :id (do not collapse to list) */}
          {LEGACY_DETAIL_REDIRECTS.map(({ from, toPrefix }) => (
            <Route key={from} path={from} element={<LegacyParamRedirect toPrefix={toPrefix} />} />
          ))}
        </Route>

        {/* T-528 Mobile Link phone app — public, own session, never the desktop auth store */}
        <Route path="/m" element={<ErrorBoundary><Suspense fallback={null}><MobileApp /></Suspense></ErrorBoundary>} />

        {/* Customer Self-Service Portal — separate auth, mobile-first, no sidebar */}
        {/* MUST be before the catch-all '*' route or it will never match */}
        <Route path="/portal/login" element={<ErrorBoundary><PortalLoginPage /></ErrorBoundary>} />
        <Route path="/portal/dashboard" element={<ErrorBoundary><PortalDashboardPage /></ErrorBoundary>} />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}
